import { Typography, Box, Paper } from '@mui/material';
import React, { useContext, useState, useEffect } from 'react';
import Content from '../components/Layout/Content';
import { BreadcrumbComponent } from '../components/BreadcrumbComponent/BreadcrumbComponent';
import ReusableTable from '../components/ReusableTable/ReusableTable';
import FilterDropDown from '../components/filterDropDown/FilterDropDown';
import SearchBar from '../components/SearchBar/Search';
import BillsSummary from '../components/bills/BillsSummary';
import ApplicationContext from '../context/applicationContext';
import NotFound from '../components/NotFound/NotFoundPage';
import { useSnackbar } from 'notistack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { SimpleBackdrop } from '../components/SimpleBackdropComponent/SimpleBackdrop';
import PaginationComponent from '../components/paginationComponent/ServerSidePaginationComponent';
import {
  dateFilterMenuItems,
  billsStatusFilterMenuItems,
  DEFAULT_NULL_REPLACEMENT,
  NUMBER_OF_ROWS_PER_PAGE,
  BILLS_STATUS_LIST,
} from '../shared/constants';
import {
  handleTimeFilter,
  handleStatusFilter,
  searchDataByKey,
  getIconFromSubItemsArray,
  arrangeBillsData,
  fetchStatusObj,
  calculateDateFilterTimeStamp,
  getStatusFilterParamList,
} from '../shared/helper';
import { ServiceFlyout } from '../components/ServiceFlyout/ServiceFlyout';
import { useLazyQuery } from '@apollo/client';
import {
  FETCH_ALL_BILLS,
  FETCH_BILL_DETAILS,
} from '../services/BillsQueries/BillsService';
import {
  BILLS,
  BILLS_TABLE_HEADING as tableHeading,
  BILLS_BREADCRUMBDATA as breadcrumbData,
} from '../shared/Strings';

const boxContainer = {
  position: 'relative',
  display: 'flex',
  flexWrap: 'wrap',
  top: 40,
  flex: 1,
};
const table = {
  marginTop: 8,
  '& .css-1kn3lhz': {},
};

const renderStatusView = (status, color) => {
  const statusObj =
    status && status !== '-'
      ? fetchStatusObj(status, color)
      : { status: '-', color: null };
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {statusObj.color && (
        <FiberManualRecordIcon
          htmlColor={statusObj.color}
          sx={{ height: '12px', width: '12px', mr: 1 }}
        />
      )}
      <Typography variant="h5">{statusObj.status}</Typography>
    </Box>
  );
};

const BillsDetails = modalTopCardData => {
  return (
    <Box
      sx={{
        maxWidth: '576px',
        height: '158px',
        bgcolor: 'rgba(248,248,248,0.8)',
        borderRadius: '8px',
        mt: '40px',
      }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
        }}>
        <Box sx={{ flexDirection: 'column', p: 1 }}>
          <Typography variant="h5" sx={{ color: '#666666', mt: '24px' }}>
            Amount{' '}
            {modalTopCardData?.status.toLowerCase() === 'balanced'
              ? 'paid'
              : 'due'}
          </Typography>
          <Typography variant="h2" sx={{ mt: '4px' }}>
            {modalTopCardData?.amount || DEFAULT_NULL_REPLACEMENT}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
          mt: '12px',
        }}>
        <Typography variant="h5" sx={{ pr: 0.5 }}>
          {renderStatusView(modalTopCardData?.status)}
        </Typography>
        <Typography variant="h5">
          on {modalTopCardData?.date || DEFAULT_NULL_REPLACEMENT}
        </Typography>
      </Box>
    </Box>
  );
};

const PaidModalData = ({
  amount,
  planName,
  paymentID,
  copayAmount,
  promoCode,
  paidAmount,
  paidByPlanAmount,
  serviceType,
}) => {
  return (
    <Box>
      <Typography variant="nav">{BILLS.PAYMENT_DETAILS}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', mt: '24px' }}>
        <Box>
          <Typography>{BILLS.PAYMENT_ID}</Typography>
          <Typography>{paymentID || DEFAULT_NULL_REPLACEMENT}</Typography>
        </Box>
        <Box sx={{ ml: '200px' }}>
          <Typography>{BILLS.PAID_BY_PLAN}</Typography>
          <Typography>
            {paidByPlanAmount !== ("-" || null || undefined) ? `$${paidByPlanAmount}` : '-'}
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ mt: '24px' }}>{BILLS.SERVICES}</Typography>
      <Paper variant="outlined" sx={{ borderRadius: '8px', p: 2, flex: 1 }}>
        <Typography>{serviceType}</Typography>
      </Paper>
      <Typography sx={{ mt: '24px' }}>{BILLS.PAYMENT_BREAKUP}</Typography>

      <Paper variant="outlined" sx={{ borderRadius: '8px', p: 2, flex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
          }}>
          <Typography variant="h5">{BILLS.COPAY_AMOUNT}</Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {copayAmount !== ("-" || null || undefined) ? `$${copayAmount}` : '-'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mt: '26px',
          }}>
          <Typography variant="h5">{BILLS.PROMO_CODE} </Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {promoCode !== (null || undefined)? `$${promoCode}` : '-'}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'space-between',
            mt: '24px',
          }}>
          <Typography variant="h5">{BILLS.PAID_AMOUNT}</Typography>
          <Typography sx={{ fontWeight: 600 }}>
            {paidAmount !== (null || undefined || "-") ? `$${paidAmount}` : '-'}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

export const Bills = () => {
  const [searchValue, setSearchValue] = useState('');
  const [recordsData, setRecordedData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [timeFilter, setTimeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [openFlyout, setOpenFlyout] = useState(false);
  const { menuItems } = useContext(ApplicationContext);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [detailsData, setDetailsData] = useState([]);
  const [modalTopCardData, setModalTopCardData] = useState();
  const [modalPaidChildCardData, setModalPaidChildCardData] = useState();
  const [modalTitle, setModalTitle] = useState('');
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState();

  const { enqueueSnackbar } = useSnackbar();

  const closeFlyout = () => {
    setOpenFlyout(false);
  };

  const handleChangeFilter = () => {
     setOffset(0);
   };

  const [fetchAllBills, fetchAllBillsCall] = useLazyQuery(FETCH_ALL_BILLS, {
    fetchPolicy: 'cache-and-network',
    onError: err => {
      enqueueSnackbar(BILLS.ON_ERROR_TEXT.WENT_WRONG_TEXT, {
        variant: 'error',
      });
    },
    onCompleted: res => {
      const paidBill = res.fetchAllBills.paidBills.map(data =>
        arrangeBillsData(data, true)
      );
      const unPaidBill = res.fetchAllBills.unPaidBills.map(data =>
        arrangeBillsData(data, false)
      );
      setTotalCount(res.fetchAllBills.totalBills);
      let result = [...paidBill, ...unPaidBill];
      setRecordedData(result);
      setFilteredData(result);
    },
  });

  const [fetchBillInfo, fetchBillInfoCall] = useLazyQuery(FETCH_BILL_DETAILS, {
    fetchPolicy: 'cache-and-network',
    onError: err => {
      enqueueSnackbar(BILLS.ON_ERROR_TEXT.BY_ID, {
        variant: 'error',
      });
    },
    onCompleted: res => {
      showFlyout();
      setModalPaidChildCardData(prevField => ({
        ...prevField,
        copayAmount: res?.fetchBillInfo?.copayAmount,
        paidAmount: res?.fetchBillInfo?.paidAmount,
        promoCode: res?.fetchBillInfo?.promoCodeAmount,
        paidByPlanAmount: res?.fetchBillInfo?.paidByPlanAmount,
        serviceType: res?.fetchBillInfo?.serviceType,
      }));
    },
  });

  useEffect(() => {
    fetchAllBills({
      variables: {
        sortOrder: 'ASC',
        sourceUrl: '',
        count: NUMBER_OF_ROWS_PER_PAGE.toString(),
        offset: offset,
        dateFilter: calculateDateFilterTimeStamp(timeFilter) || 0,
        statusFilter: getStatusFilterParamList(statusFilter, billsStatusFilterMenuItems, BILLS_STATUS_LIST),
        searchFilter: searchValue,
      },
    });
  }, [offset, statusFilter, timeFilter, searchValue]);

  const onPageClick = pageNumber => {
    setOffset((pageNumber - 1) * 10);
  };

  const onSearch = text => {
    setSearchValue(text);
    offset !== 0 && setOffset(0);
  };

  const dateFilterOptions = {
    options: dateFilterMenuItems,
    value: timeFilter,
    changeHandler: ev => {
      setTimeFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const statusFilterOptions = {
    options: billsStatusFilterMenuItems,
    value: statusFilter,
    changeHandler: ev => {
      setStatusFilter(ev.target.value);
      handleChangeFilter();
    },
  };

  const detailsLoading = fetchBillInfoCall.loading;

  const showFlyout = (id) => {
    const tableRowInfo = recordsData.find(obj => obj.id === id);
    setModalTopCardData({
      amount: tableRowInfo.amount,
      date: tableRowInfo.recordedOn,
      status: tableRowInfo.status,
      pay: tableRowInfo.pay,
    });

    const modalData = [
      {
        column_name: BILLS.MODAL_DATA.SERVICE_DATE,
        value: tableRowInfo.recordedOn || DEFAULT_NULL_REPLACEMENT,
        width: 6,
      },
      {
        column_name: BILLS.MODAL_DATA.FACILITY,
        value: tableRowInfo.providerName || DEFAULT_NULL_REPLACEMENT,
      },
    ];
    setDetailsData(modalData);
    setOpenFlyout(true);
  };

  const viewDetailsHandler = id => {
    setSelectedTableId(id);
    const tableRowInfo = filteredData.find(obj => obj.id === id);
    if (tableRowInfo.status === 'BALANCED') {
      setModalPaidChildCardData({
        amount: tableRowInfo.amount,
        paymentStatus: tableRowInfo.status,
        // paymentID: tableRowInfo.paymentID, // no payment ID found in response, hence commenting this out.
        copayAmount: tableRowInfo.copayAmount,
        paidAmount: tableRowInfo.paidAmount,
        promoCode: tableRowInfo.promoCodeAmount,
        paidByPlanAmount: tableRowInfo.paidByPlanAmount,
        serviceType: tableRowInfo.serviceType,
      });
    }
    setModalTitle(tableRowInfo.facility);
    setModalTopCardData({
      amount: tableRowInfo.amount,
      date: tableRowInfo.recordedOn,
      status: tableRowInfo.status,
    });

    showFlyout(id);

  //   fetchBillInfo({
  //     variables: {
  //       billId: id, // query not working, hence commenting it out and getting response from the row itself
  //     },
  //   });
  };

  return (
    <Content>
      <BreadcrumbComponent
        breadcrumbData={breadcrumbData}></BreadcrumbComponent>
      <Typography variant="h1">{BILLS.TITLE_TEXT}</Typography>
      <Typography variant="h5" sx={{ mt: '40px' }}>
        {BILLS.BILL_SUMMARY}
      </Typography>
      <BillsSummary />
      <Box sx={boxContainer}>
        <SearchBar
          sx={{
            width: '50%',
            minWidth: '250px',
            maxWidth: '500px',
            marginTop: '8px',
          }}
          onSearch={onSearch}
          placeholder={BILLS.SEARCH_PLACEHOLDER_TEXT}
        />
        <FilterDropDown {...dateFilterOptions} filterType="Date:" />
        <FilterDropDown {...statusFilterOptions} filterType="Status:" />
      </Box>
      <Box sx={table}>
        {!fetchAllBillsCall.loading && !filteredData.length ? (
          <NotFound title={BILLS.TITLE_TEXT} />
        ) : (
          <>
            <ReusableTable
              tableHeading={tableHeading}
              data={filteredData}
              viewDetailsHandler={viewDetailsHandler}
            />
            <div
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                marginTop: 18,
              }}>
              {totalCount > NUMBER_OF_ROWS_PER_PAGE && (
                <PaginationComponent
                  numberOfRowsPerPage={NUMBER_OF_ROWS_PER_PAGE}
                  count={totalCount}
                  onPageClick={onPageClick}
                  page={offset / 10}
                />
              )}
            </div>
          </>
        )}
      </Box>
      <ServiceFlyout
        isOpen={openFlyout}
        handleClose={closeFlyout}
        data={detailsData}
        icon={
          menuItems
            ? getIconFromSubItemsArray(menuItems?.Financial?.subItems, 'Bills')
            : null
        }
        category={BILLS.TITLE_TEXT}
        topItem={BillsDetails(modalTopCardData)}
        title={modalTitle}>
        {modalTopCardData?.status.toLowerCase() === 'balanced' && (
          <PaidModalData
            amount={modalPaidChildCardData.amount}
            copayAmount={modalPaidChildCardData.copayAmount}
            paymentID={modalPaidChildCardData.paymentID}
            planName={modalPaidChildCardData.planName}
            promoCode={modalPaidChildCardData.promoCode}
            paidAmount={modalPaidChildCardData.paidAmount}
            paidByPlanAmount={modalPaidChildCardData.paidByPlanAmount}
            serviceType={modalPaidChildCardData.serviceType}
          />
        )}
      </ServiceFlyout>
      <SimpleBackdrop open={fetchAllBillsCall.loading || detailsLoading} />
    </Content>
  );
};
